/**
 * "You may also like" strip in the cart drawer (see snippets/srx-cart-recs.liquid).
 *
 * Loaded from cart-drawer.liquid rather than inline in the snippet: the snippet's
 * markup arrives via the Section Rendering API morph after the first add-to-cart,
 * and scripts injected that way never execute.
 */
const usd = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

class SrxCartRecs extends HTMLElement {
  static observedAttributes = ['data-product-id', 'data-in-cart'];
  #loadedFor = null;

  connectedCallback() {
    this.#load();
  }

  attributeChangedCallback() {
    if (this.isConnected) this.#load();
  }

  async #load() {
    const productId = this.dataset.productId;
    const inCart = new Set((this.dataset.inCart || '').split(',').filter(Boolean));
    const key = productId + '|' + [...inCart].join(',');
    if (!productId || key === this.#loadedFor) return;
    this.#loadedFor = key;

    try {
      const root = window.Shopify?.routes?.root || '/';
      const res = await fetch(
        `${root}recommendations/products.json?product_id=${productId}&limit=8&intent=related`,
        { headers: { Accept: 'application/json' } }
      ).then((r) => r.json());

      let products = (res.products || []).filter((p) => !inCart.has(String(p.id)));

      // Sparse catalogs can return nothing for "related" — fall back to the
      // storefront product list so the strip never sits empty.
      if (!products.length) {
        const fallback = await fetch(`${root}products.json?limit=12`, { headers: { Accept: 'application/json' } })
          .then((r) => r.json())
          .catch(() => null);
        products = (fallback?.products || []).filter((p) => !inCart.has(String(p.id)));
      }

      products = products.slice(0, 4);
      const row = this.querySelector('[data-srx-recs-row]');
      if (!row || !products.length) {
        this.hidden = true;
        return;
      }

      row.textContent = '';
      for (const p of products) {
        const card = document.createElement('a');
        card.className = 'srx-cart-recs__card';
        card.href = p.url || `${root}products/${p.handle}`;

        const image = p.featured_image || p.images?.[0]?.src;
        const img = document.createElement('img');
        img.loading = 'lazy';
        img.width = 120;
        img.height = 120;
        img.alt = p.title;
        if (image) img.src = image + (image.includes('?') ? '&' : '?') + 'width=240';

        const name = document.createElement('span');
        name.className = 'srx-cart-recs__name';
        name.textContent = p.title;

        const cents = p.price != null ? Number(p.price) : Number(p.variants?.[0]?.price || 0) * 100;
        const price = document.createElement('span');
        price.className = 'srx-cart-recs__price';
        price.textContent = usd.format(cents / 100);

        card.append(img, name, price);
        row.appendChild(card);
      }
      this.hidden = false;
    } catch {
      this.hidden = true;
    }
  }
}

if (!customElements.get('srx-cart-recs')) {
  customElements.define('srx-cart-recs', SrxCartRecs);
}
